<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { CustomFieldInfo } from '@/types/classifiedOrder';
import type { ListingPresentation } from '@/types/proof';
import { scaleAdFontSizes } from '@/utils/fontScaler';
import { buildDisplayCustomFields } from '@/utils/customFieldDisplay';
import { useBrowseListingsStore } from '@/stores/browseListings';

const props = defineProps<{
  ad: ListingPresentation;
  customFieldDefinitions?: CustomFieldInfo[];
  inline?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const browseStore = useBrowseListingsStore();

const currentImageIndex = ref(0);
const showTextSection = ref(false);

// Touch swipe support for mobile image navigation
const touchStartX = ref<number | null>(null);
const touchStartY = ref<number | null>(null);

// PDF Builder ad image URL - prefer cached URL from API, fallback to Base64 data URL
// Adds cache-busting parameter to ensure updated images are displayed
const pdfBuilderImageUrl = computed(() => {
  if (props.ad.isPdfBuilderAd) {
    // Prefer cached URL from Azure Blob Storage (when pdfImageUrls=true)
    if (props.ad.pdfBuilderImageUrl) {
      if (props.ad.pdfBuilderImageUrl.startsWith('data:') || props.ad.pdfBuilderImageUrl.startsWith('blob:')) return props.ad.pdfBuilderImageUrl;
      // Add cache-busting parameter using order end date (changes when ad is modified)
      const cacheBuster = props.ad.orderEndDate 
        ? new Date(props.ad.orderEndDate).getTime() 
        : Date.now();
      const separator = props.ad.pdfBuilderImageUrl.includes('?') ? '&' : '?';
      return `${props.ad.pdfBuilderImageUrl}${separator}v=${cacheBuster}`;
    }
    // Fallback to Base64 data URL for backward compatibility
    if (props.ad.pdfBuilderAdCopy) {
      return `data:image/jpeg;base64,${props.ad.pdfBuilderAdCopy}`;
    }
  }
  return null;
});

// Check if this is a PDF Builder ad
const isPdfBuilderAd = computed(() => props.ad.isPdfBuilderAd);

// Combine all images into a single array (PDF Builder image first if present)
const allImages = computed(() => {
  const images: string[] = [];
  
  // PDF Builder ad image first
  if (pdfBuilderImageUrl.value) {
    images.push(pdfBuilderImageUrl.value);
  }
  
  if (props.ad.printImages && props.ad.printImages.length > 0) {
    images.push(...props.ad.printImages);
  }
  
  if (props.ad.webFiles && props.ad.webFiles.length > 0) {
    images.push(...props.ad.webFiles);
  }
  
  return images;
});

const hasImages = computed(() => allImages.value.length > 0);
const hasMultipleImages = computed(() => allImages.value.length > 1);

const adText = computed(() => {
  const raw = props.ad.adTextFormatted || props.ad.adTextPlain || '';
  return scaleAdFontSizes(raw);
});

// Check if there's actual ad text content (not just empty/whitespace)
// For PDF Builder ads, adTextPlain might be empty but adTextFormatted might have HTML
const hasAdText = computed(() => {
  // First check plain text
  const plainText = props.ad.adTextPlain || '';
  if (plainText.trim().length > 0) return true;
  
  // Also check formatted text (strip HTML tags to check for actual content)
  const formattedText = props.ad.adTextFormatted || '';
  const strippedText = formattedText.replace(/<[^>]*>/g, '').trim();
  return strippedText.length > 0;
});

// Custom fields for display in modal (all fields, full values)
const customFields = computed(() => {
  if (!props.ad.customFieldValues || props.ad.customFieldValues.length === 0) {
    return [];
  }
  
  // Get custom field definitions for this ad's classification
  const fieldDefinitions = props.customFieldDefinitions ?? browseStore.getCustomFieldsForClassification(props.ad.classificationKey);
  
  // No limit for modal, show full values
  return buildDisplayCustomFields(
    props.ad.customFieldValues,
    fieldDefinitions,
    0, // no truncation for modal
    0  // no limit - show all fields
  );
});

function toggleText() {
  showTextSection.value = !showTextSection.value;
}

function nextImage() {
  if (currentImageIndex.value < allImages.value.length - 1) {
    currentImageIndex.value++;
  } else {
    currentImageIndex.value = 0;
  }
}

function prevImage() {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  } else {
    currentImageIndex.value = allImages.value.length - 1;
  }
}

function handleOverlayClick(event: MouseEvent) {
  // Only close if clicking on the overlay, not the modal content
  if (event.target === event.currentTarget) {
    emit('close');
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    emit('close');
  } else {
    handleImageKeydown(event);
  }
}

/** Navigates photos without consuming keys used by editable controls or the parent viewer. */
function handleImageKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented || !hasMultipleImages.value || event.altKey || event.ctrlKey || event.metaKey) return;
  const target = event.target;
  if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="tab"]')) return;
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    prevImage();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    nextImage();
  }
}

// An inline viewer owns its keyboard scope; standalone details use the document listener below.
defineExpose({ handleImageKeydown });

// Touch swipe handlers for mobile image navigation
function handleTouchStart(event: TouchEvent) {
  if (!hasMultipleImages.value) return;
  const touch = event.touches[0];
  touchStartX.value = touch.clientX;
  touchStartY.value = touch.clientY;
}

function handleTouchEnd(event: TouchEvent) {
  if (!hasMultipleImages.value || touchStartX.value === null || touchStartY.value === null) return;
  
  const touch = event.changedTouches[0];
  const deltaX = touch.clientX - touchStartX.value;
  const deltaY = touch.clientY - touchStartY.value;
  
  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;
  
  // Only trigger swipe if horizontal movement is greater than vertical (prevents accidental swipes while scrolling)
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
    if (deltaX > 0) {
      // Swipe right - go to previous image
      prevImage();
    } else {
      // Swipe left - go to next image
      nextImage();
    }
  }
  
  // Reset touch tracking
  touchStartX.value = null;
  touchStartY.value = null;
}

onMounted(() => {
  if (props.inline) return;
  document.addEventListener('keydown', handleKeydown);
  document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
  if (props.inline) return;
  document.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body" :disabled="inline">
    <div :class="inline ? 'listing-inline' : 'modal-overlay'" @click="!inline && handleOverlayClick($event)">
      <div class="modal-wrapper">
        <!-- Close button - positioned at top-right corner of modal -->
        <button v-if="!inline" class="close-btn" @click="emit('close')" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div :class="inline ? 'listing-content' : 'modal-content'">
          <!-- Image carousel -->
          <div v-if="hasImages" class="image-section">
            <div
              class="image-carousel"
              @touchstart="handleTouchStart"
              @touchend="handleTouchEnd"
            >
              <img 
                :src="allImages[currentImageIndex]" 
                :alt="`Listing image ${currentImageIndex + 1}`"
                class="carousel-image"
              />
              
              <!-- Navigation arrows -->
              <template v-if="hasMultipleImages">
                <button class="carousel-btn carousel-btn--prev" @click.stop="prevImage" aria-label="Previous image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <button class="carousel-btn carousel-btn--next" @click.stop="nextImage" aria-label="Next image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </template>
            </div>
            
            <!-- Thumbnail indicators -->
            <div v-if="hasMultipleImages" class="image-indicators">
              <button 
                v-for="(_, index) in allImages" 
                :key="index"
                class="indicator"
                :class="{ 'indicator--active': index === currentImageIndex }"
                @click.stop="currentImageIndex = index"
                :aria-label="`View image ${index + 1}`"
              />
            </div>
          </div>

          <!-- For PDF Builder ads: show toggle button when text is hidden (only if there's actual text) -->
          <div v-if="isPdfBuilderAd && hasAdText && !showTextSection" class="show-text-toggle">
            <button @click="toggleText" class="show-text-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
              Show Text (for copying)
            </button>
          </div>

          <!-- Two-column layout when we have both text and custom fields (non-PDF Builder) -->
          <div v-if="hasAdText && customFields.length > 0 && !isPdfBuilderAd" class="content-columns">
            <div class="text-column">
              <div class="listing-text-content" v-html="adText"></div>
            </div>
            <div class="details-column">
              <h3 class="section-title">Details</h3>
              <dl class="custom-fields-list">
                <div v-for="cf in customFields" :key="cf.key" class="field-row">
                  <dt>{{ cf.name }}</dt>
                  <dd>{{ cf.fullValue }}</dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Single column: Text only (no custom fields, or PDF Builder with text toggle) -->
          <div v-else-if="hasAdText && (!isPdfBuilderAd || showTextSection)" class="text-section">
            <button v-if="isPdfBuilderAd" @click="toggleText" class="hide-text-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Hide Text
            </button>
            <div class="listing-text-content" v-html="adText"></div>
          </div>

          <!-- Boxed details stay visible independently of the optional text section. -->
          <div v-if="customFields.length > 0 && (isPdfBuilderAd || !hasAdText)" class="custom-fields-section">
            <h3 class="section-title">Details</h3>
            <dl class="custom-fields-list">
              <div v-for="cf in customFields" :key="cf.key" class="field-row">
                <dt>{{ cf.name }}</dt>
                <dd>{{ cf.fullValue }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.listing-inline .listing-content { max-height: none; overflow: hidden; box-shadow: none; }
.listing-inline .modal-wrapper { max-width: none; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.modal-wrapper {
  position: relative;
  max-width: 900px;
  width: 100%;
}

.modal-content, .listing-content {
  background: var(--bg-primary, #fff);
  border-radius: 12px;
  max-height: 90vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Close button - positioned at top-right corner of modal */
.close-btn {
  position: absolute;
  top: -20px;
  right: -20px;
  background: #333;
  border: 3px solid white;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  z-index: 1001;
  transition: background 0.2s, transform 0.2s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

.close-btn:hover {
  background: #555;
  transform: scale(1.1);
}

/* Image Section */
.image-section {
  flex-shrink: 0;
  background: #f0f0f0;
}

.image-carousel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.carousel-image {
  max-width: 100%;
  max-height: 70vh; /* Larger height for better readability */
  object-fit: contain;
  image-orientation: from-image; /* Respect EXIF rotation data */
  display: block;
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background 0.2s;
}

.carousel-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.carousel-btn--prev {
  left: 12px;
}

.carousel-btn--next {
  right: 12px;
}

.image-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.05);
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s;
}

.indicator:hover {
  background: rgba(0, 0, 0, 0.4);
}

.indicator--active {
  background: var(--button-background);
  transform: scale(1.2);
}

/* Show Text Toggle (for PDF Builder ads) */
.show-text-toggle {
  padding: 12px 24px;
  background: var(--bg-secondary, #f5f5f5);
  border-top: 1px solid var(--border-color, #e0e0e0);
}

.show-text-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--button-background);
  color: var(--button-text-color);
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.show-text-btn:hover {
  background: var(--button-background-hover);
}

.hide-text-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  color: var(--text-secondary, #666);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.8125rem;
  cursor: pointer;
  margin-bottom: 16px;
  transition: background 0.2s, color 0.2s;
}

.hide-text-btn:hover {
  background: var(--bg-secondary, #f5f5f5);
  color: var(--text-primary, #1a1a1a);
}

/* Two-column layout for text + custom fields */
.content-columns {
  display: flex;
  flex: 1;
  min-height: 0; /* Allow flex children to scroll */
  border-top: 1px solid var(--border-color, #e0e0e0);
}

.text-column {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  border-right: 1px solid var(--border-color, #e0e0e0);
}

.details-column {
  flex: 0 0 280px;
  padding: 20px;
  overflow-y: auto;
  background: var(--bg-secondary, #f9f9f9);
}

.details-column .section-title {
  margin-top: 0;
}

/* Text Section (single column fallback) */
.text-section {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.listing-text-content {
  /* Larger, more readable text for modal view */
  font-size: 1.375rem; /* ~22px - noticeably larger than card base */
  line-height: 1.4;
  color: var(--text-primary, #1a1a1a);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Override any inline font sizes from the HTML - force minimum readable size */
.listing-text-content :deep(*) {
  font-size: inherit !important;
  line-height: inherit !important;
}

.listing-text-content :deep(p) {
  margin: 0 0 1em 0;
}

.listing-text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.listing-text-content :deep(strong),
.listing-text-content :deep(b) {
  font-weight: 600;
}

/* Custom Fields Section */
.custom-fields-section {
  padding: 20px 24px;
  border-top: 1px solid var(--border-color, #e0e0e0);
  background: var(--bg-secondary, #f9f9f9);
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary, #666);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 12px 0;
}

.custom-fields-list {
  margin: 0;
  display: grid;
  gap: 8px;
}

.field-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: var(--bg-primary, #fff);
  border-radius: 6px;
  border: 1px solid var(--border-color, #e8e8e8);
}

.field-row dt {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-tertiary, #888);
}

.field-row dd {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-primary, #1a1a1a);
  word-break: break-word;
}

/* Mobile responsive */
@media (max-width: 700px) {
  .modal-overlay {
    padding: 0;
  }
  
  .modal-wrapper {
    max-width: 100%;
  }
  
  .modal-content {
    max-height: 100vh;
    border-radius: 0;
  }
  
  .close-btn {
    top: 8px;
    right: 8px;
  }
  
  /* Stack columns on mobile */
  .content-columns {
    flex-direction: column;
  }
  
  .text-column {
    border-right: none;
    border-bottom: 1px solid var(--border-color, #e0e0e0);
  }

  .details-column {
    flex: none;
  }
  
  .text-section {
    padding: 20px;
  }
  
  .listing-text-content {
    font-size: 1.25rem;
    line-height: 1.4;
  }
  
  .custom-fields-section {
    padding: 16px 20px;
  }
  
  .field-row dt {
    min-width: unset;
  }
}
</style>
